import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Alert, AlertDescription } from './ui/alert';
import { CheckCircle, ArrowLeft, Lock } from 'lucide-react';
import { resetPassword } from '../api/auth';

interface ResetPasswordPageProps {
  token: string;
  onSuccess: () => void;
  onInvalidToken: () => void;
}

export function ResetPasswordPage({ token, onSuccess, onInvalidToken }: ResetPasswordPageProps) {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token || token.length < 10) {
      onInvalidToken();
    }
  }, [token, onInvalidToken]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(password)) {
      setError('Password must contain at least one uppercase letter, one lowercase letter, and one number');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      await resetPassword(token, password);
      setSuccess(true);
      setTimeout(() => onSuccess(), 2500);
    } catch (err: any) {
      setError(err?.message || 'Failed to reset password. The link may have expired.');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return null;
  }

  return (
    <div className="min-h-screen w-full bg-[var(--navy)] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-3">
            <img src="/images/Corrected%20Logo.png" alt="Insolip" className="h-16 w-auto" />
          </div>
          <p className="text-white/70 text-sm">Set a new password for your account</p>
        </div>

        <Card className="bg-white border-[rgba(201,168,76,0.25)] shadow-xl">
          <CardHeader>
            <CardTitle className="text-[var(--navy)] font-display font-semibold flex items-center gap-2">
              <Lock className="w-5 h-5 text-[var(--gold)]" />
              Reset password
            </CardTitle>
            <CardDescription className="text-[var(--navy)]/80">
              Enter your new password below. Use at least 8 characters with uppercase, lowercase, and a number.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {success ? (
              <Alert className="bg-[var(--gold-pale)] border-[rgba(201,168,76,0.3)]">
                <CheckCircle className="h-4 w-4 text-[var(--gold)]" />
                <AlertDescription className="text-[var(--navy)]">
                  Your password has been reset. Redirecting you to sign in…
                </AlertDescription>
              </Alert>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="reset-password" className="text-[var(--navy)]">New password</Label>
                  <Input
                    id="reset-password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => { setPassword(e.target.value); setError(null); }}
                    required
                    minLength={8}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="reset-confirm" className="text-[var(--navy)]">Confirm password</Label>
                  <Input
                    id="reset-confirm"
                    type="password"
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => { setConfirmPassword(e.target.value); setError(null); }}
                    required
                    minLength={8}
                  />
                </div>
                {error && (
                  <Alert variant="destructive">
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}
                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? 'Resetting…' : 'Reset password'}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  className="w-full text-[var(--navy)]/80"
                  onClick={onSuccess}
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to sign in
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
